import { useQuery } from '@tanstack/react-query';
import { DATASET_URL_KEY } from '@/utils/queryKeys.ts';
import { AxiosError } from 'axios';
import { get_dataset_presigned_url } from '@/utils/urls.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient';
import { UrlResponse } from '@/types/common.ts';

const FALLBACK_SIGNED_URL_STALE_MS = 45 * 60 * 1000;
const SIGNED_URL_REFRESH_BUFFER_MS = 5 * 60 * 1000;

function getSignedUrlRefreshMs(data?: UrlResponse) {
  if (!data?.expires_at) {
    return FALLBACK_SIGNED_URL_STALE_MS;
  }

  const expiresAtMs = Date.parse(data.expires_at);
  if (Number.isNaN(expiresAtMs)) {
    return FALLBACK_SIGNED_URL_STALE_MS;
  }

  return Math.max(expiresAtMs - Date.now() - SIGNED_URL_REFRESH_BUFFER_MS, 0);
}

const useGetDatasetUrlQuery = (datasetId: string | undefined, enabled: boolean) => {
  const apiClient = useApiClient();

  return useQuery<UrlResponse, AxiosError>({
    queryKey: DATASET_URL_KEY(datasetId),
    queryFn: async () => {
      const response = await apiClient.get(get_dataset_presigned_url(datasetId), {
        params: { _ts: Date.now() },
      });
      return response.data;
    },
    enabled: !!datasetId && enabled,
    refetchInterval: (query) => {
      const refreshMs = getSignedUrlRefreshMs(query.state.data);
      return refreshMs > 0 ? refreshMs : false;
    },
    refetchOnWindowFocus: true,
    staleTime: (query) => getSignedUrlRefreshMs(query.state.data),
    refetchOnReconnect: true,
    refetchOnMount: true,
  });
};

export default useGetDatasetUrlQuery;
